import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { getContacts, Contact } from '../../contacts';
import { createAsset, uploadAssetAttachment } from '../services/assetService';
import { AssetOwner } from '../types/Asset';
import { useAssetSettings } from '../context/AssetSettingsContext';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { AssetOwnersField } from './AssetOwnersField';
import { AssetTypeFields } from './AssetTypeFields';
import { assetTypePath, assetsListPath, emptyDetailsFor, getAssetTypeBySlug, serializeDetails } from '../typeCatalog';
import { CATALOG_ATTACHMENT_MAX_COUNT, CATALOG_FILE_ACCEPT, CATALOG_IMAGE_ACCEPT, formatCatalogFileSize } from '../../catalog/catalogAttachments';
import catalog from '../../catalog/Catalog.module.css';
import { ButtonWithIcon, SaveIcon, SettingsIcon } from './AssetIcons';
import iconStyles from './AssetIcons.module.css';
import styles from './AssetLayout.module.css';

export const AddAsset: React.FC = () => {
  const { t } = useTranslation(['assets', 'common']);
  const navigate = useNavigate();
  const { typeSlug } = useParams<{ typeSlug: string }>();
  const [searchParams] = useSearchParams();
  const preselectedContactId = (searchParams.get('contactId') || '').trim();
  const module = getAssetTypeBySlug(typeSlug);
  const { enabledTypes } = useAssetSettings();
  const { hasPermission } = useAuth();
  const typeEnabled = Boolean(module && enabledTypes.includes(module.type));
  const canUploadAfterCreate = hasPermission(PermissionScopes.assetsUpdate);

  const [contacts, setContacts] = React.useState<Contact[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    name: '',
    identifier: '',
    owners: preselectedContactId
      ? [{ contactPersonId: preselectedContactId, isRegistered: true, isPrimary: true }] as AssetOwner[]
      : [] as AssetOwner[],
    details: module ? emptyDetailsFor(module.type) : {},
  });
  const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
  const [pendingFiles, setPendingFiles] = React.useState<File[]>([]);
  const imageInputRef = React.useRef<HTMLInputElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (module) {
      setForm(prev => ({ ...prev, details: emptyDetailsFor(module.type) }));
    }
  }, [module]);

  React.useEffect(() => {
    const loadContacts = async () => {
      setLoading(true);
      try {
        const availableContacts = await getContacts();
        setContacts(availableContacts);
      } catch {
        setGeneralError(t('failedToLoadAssets', { ns: 'assets' }));
      } finally {
        setLoading(false);
      }
    };

    void loadContacts();
  }, [t]);

  const validate = () => {
    const nextErrors: { [key: string]: string } = {};
    if (!form.name.trim()) nextErrors.name = t('nameRequired', { ns: 'assets' });
    if (form.owners.length === 0) nextErrors.owners = t('contactRequired', { ns: 'assets' });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const addPendingFiles = (selected: File[]) => {
    if (selected.length === 0) return;
    setPendingFiles(previous => {
      const remaining = Math.max(0, CATALOG_ATTACHMENT_MAX_COUNT - previous.length);
      return remaining === 0 ? previous : [...previous, ...selected.slice(0, remaining)];
    });
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!module || !validate()) return;

    setSaving(true);
    setGeneralError(null);

    try {
      const created = await createAsset({
        name: form.name.trim(),
        assetType: module.type,
        identifier: form.identifier.trim() || undefined,
        owners: form.owners,
        details: serializeDetails(module.type, form.details),
      });

      if (!created) {
        setGeneralError(t('failedToAddAsset', { ns: 'assets' }));
        return;
      }

      try {
        let firstImageUsed = false;
        for (const file of pendingFiles) {
          const setAsPrimary = !firstImageUsed && file.type.startsWith('image/');
          if (setAsPrimary) {
            firstImageUsed = true;
          }
          await uploadAssetAttachment(created.id, file, setAsPrimary);
        }
      } catch {
        navigate(assetTypePath(created.assetType, `/${created.id}`));
        return;
      }

      navigate(assetTypePath(created.assetType, `/${created.id}`));
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToAddAsset', { ns: 'assets' }));
    } finally {
      setSaving(false);
    }
  };

  if (!module) {
    return null;
  }

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loadingAssets', { ns: 'assets' })} /></div>;
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={[
        { label: t(module.labelKey, { ns: 'assets' }), path: assetsListPath(module.type) },
        { label: t('addTitle', { ns: 'assets' }), path: assetTypePath(module.type, '/add') },
      ]} />

      <h2 className={styles.title}>{t('addTitle', { ns: 'assets' })} · {t(module.labelKey, { ns: 'assets' })}</h2>
      {generalError ? <div className="error">{generalError}</div> : null}
      {!typeEnabled ? (
        <p className={styles.hint}>
          {t('noEnabledTypesHint', { ns: 'assets' })}{' '}
          <button type="button" className={`btn btn-secondary ${iconStyles.buttonWithIcon}`} onClick={() => navigate('/assets/settings')}>
            <ButtonWithIcon icon={<SettingsIcon />}>{t('settings', { ns: 'assets' })}</ButtonWithIcon>
          </button>
        </p>
      ) : null}

      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label className={styles.label}>{t('name', { ns: 'assets' })}</label>
            <input className={styles.input} value={form.name} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} />
            {errors.name ? <div className={styles.fieldError}>{errors.name}</div> : null}
          </div>
          <div className={styles.field}>
            <label className={styles.label}>{t(module.identifierLabelKey, { ns: 'assets' })}</label>
            <input className={styles.input} value={form.identifier} onChange={event => setForm(prev => ({ ...prev, identifier: event.target.value }))} />
          </div>
        </div>

        <AssetTypeFields
          module={module}
          values={form.details}
          onChange={details => setForm(prev => ({ ...prev, details }))}
        />

        <AssetOwnersField
          contacts={contacts}
          owners={form.owners}
          onChange={owners => setForm(prev => ({ ...prev, owners }))}
          error={errors.owners}
        />

        {canUploadAfterCreate ? (
          <div className={catalog.mediaPanel}>
            <input
              ref={imageInputRef}
              className={catalog.hiddenInput}
              type="file"
              accept={CATALOG_IMAGE_ACCEPT}
              multiple
              onChange={event => {
                addPendingFiles(Array.from(event.target.files ?? []));
                event.target.value = '';
              }}
            />
            <input
              ref={fileInputRef}
              className={catalog.hiddenInput}
              type="file"
              accept={CATALOG_FILE_ACCEPT}
              multiple
              onChange={event => {
                addPendingFiles(Array.from(event.target.files ?? []));
                event.target.value = '';
              }}
            />
            <section className={catalog.mediaSection}>
              <div className={catalog.mediaSectionHeader}>
                <div>
                  <p className={catalog.detailLabel}>{t('photos', { ns: 'assets' })}</p>
                  <p className={catalog.fieldHint}>{t('photosHint', { ns: 'assets' })}</p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={saving || pendingFiles.length >= CATALOG_ATTACHMENT_MAX_COUNT}
                >
                  {t('addPhotos', { ns: 'assets' })}
                </button>
              </div>
            </section>
            <section className={catalog.mediaSection}>
              <div className={catalog.mediaSectionHeader}>
                <div>
                  <p className={catalog.detailLabel}>{t('attachments', { ns: 'assets' })}</p>
                  <p className={catalog.fieldHint}>{t('attachmentsHint', { ns: 'assets' })}</p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving || pendingFiles.length >= CATALOG_ATTACHMENT_MAX_COUNT}
                >
                  {t('addAttachment', { ns: 'assets' })}
                </button>
              </div>
              {pendingFiles.length === 0 ? (
                <p className={catalog.fieldHint}>{t('noAttachments', { ns: 'assets' })}</p>
              ) : (
                <ul className={catalog.attachmentList}>
                  {pendingFiles.map((file, index) => (
                    <li key={`${file.name}-${file.size}-${index}`} className={catalog.attachmentRow}>
                      <div className={catalog.attachmentMeta}>
                        <span className={catalog.attachmentName}>{file.name}</span>
                        <span className={catalog.secondaryText}>{formatCatalogFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setPendingFiles(previous => previous.filter((_, itemIndex) => itemIndex !== index))}
                        disabled={saving}
                      >
                        {t('removeAttachment', { ns: 'assets' })}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : (
          <p className={styles.hint}>{t('photoAfterSave', { ns: 'assets' })}</p>
        )}

        <div className={styles.actions}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(assetsListPath(module.type))} disabled={saving}>{t('back', { ns: 'assets' })}</button>
          <button type="submit" className={`btn btn-primary ${iconStyles.buttonWithIcon}`} disabled={saving || !typeEnabled}>
            <ButtonWithIcon icon={<SaveIcon />}>{t('save', { ns: 'assets' })}</ButtonWithIcon>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddAsset;
